import {type HttpInterceptorFn, HttpErrorResponse} from '@angular/common/http';
import {inject} from '@angular/core';
import {Router} from '@angular/router';
import {catchError, throwError} from 'rxjs';
import {environment} from '@/environments/environment';
import {StorageKeys} from '@/app/core/enums/storage-keys';
import {AuthService} from '@/app/core/services/auth.service';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
const CLIENT_TYPES = ['client', 'tenantClient', 'Tenant Client'];
type AuthContext = 'tenant' | 'client';
let isHandlingUnauthorized = false;

function getContextFromUserType(type: string | undefined): AuthContext {
  return CLIENT_TYPES.includes(type ?? '') ? 'client' : 'tenant';
}

function getActiveAuthContext(): AuthContext | null {
  const sessionContext = sessionStorage.getItem(
    StorageKeys.ACTIVE_AUTH_CONTEXT,
  );
  if (sessionContext === 'client' || sessionContext === 'tenant') {
    return sessionContext;
  }
  return null;
}

function clearAuthStorageForContext(context: AuthContext): void {
  const contextKey =
    context === 'client'
      ? StorageKeys.USER_DATA_CLIENT
      : StorageKeys.USER_DATA_TENANT;
  sessionStorage.removeItem(contextKey);
  localStorage.removeItem(contextKey);

  for (const store of [sessionStorage, localStorage]) {
    const raw = store.getItem(StorageKeys.USER_DATA);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw);
      const rawContext = getContextFromUserType(parsed?.user?.type);
      if (rawContext === context) {
        store.removeItem(StorageKeys.USER_DATA);
      }
    } catch {
      store.removeItem(StorageKeys.USER_DATA);
    }
  }
}

function isStoredUserClient(): boolean {
  try {
    const sessionContext = sessionStorage.getItem(
      StorageKeys.ACTIVE_AUTH_CONTEXT,
    );
    const activeContext =
      sessionContext === 'client' || sessionContext === 'tenant'
        ? sessionContext
        : null;

    const contextKey =
      activeContext === 'client'
        ? StorageKeys.USER_DATA_CLIENT
        : activeContext === 'tenant'
          ? StorageKeys.USER_DATA_TENANT
          : null;

    const rawCandidates = [
      contextKey ? sessionStorage.getItem(contextKey) : null,
      contextKey ? localStorage.getItem(contextKey) : null,
      sessionStorage.getItem(StorageKeys.USER_DATA),
      localStorage.getItem(StorageKeys.USER_DATA),
      sessionStorage.getItem(StorageKeys.USER_DATA_CLIENT),
      localStorage.getItem(StorageKeys.USER_DATA_CLIENT),
      sessionStorage.getItem(StorageKeys.USER_DATA_TENANT),
      localStorage.getItem(StorageKeys.USER_DATA_TENANT),
    ];

    for (const raw of rawCandidates) {
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const type = parsed?.user?.type ?? '';
      const isClient = CLIENT_TYPES.includes(type);
      if (activeContext && (activeContext === 'client') !== isClient) continue;
      return isClient;
    }
    return false;
  } catch {
    return false;
  }
}

function isTenantOnlyUrl(url: string): boolean {
  const base = environment.baseUrl;
  if (!url.startsWith(base)) return false;
  const path = url.slice(base.length);
  return (
    path.startsWith('clients') ||
    path.startsWith('tenants') ||
    (path.startsWith('landing-pages') && !path.includes('/public/'))
  );
}

export const apiErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const message = inject(MessageService);
  const translate = inject(TranslateService);
  const isApi = req.url.startsWith(environment.baseUrl);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse)) {
        return throwError(() => err);
      }

      if (isApi && typeof ngDevMode === 'boolean' && ngDevMode) {
        const body = err.error;
        const msg =
          body && typeof body === 'object' && 'message' in body
            ? String((body as {message: unknown}).message)
            : err.message;
        console.warn(
          `[API Error] ${req.method} ${req.url} → ${err.status}`,
          msg,
          body,
        );
      }

      if (isApi && err.status === 401) {
        if (!isHandlingUnauthorized) {
          isHandlingUnauthorized = true;
          auth.logout();
          setTimeout(() => {
            isHandlingUnauthorized = false;
          }, 0);
        }
      }

      if (
        isApi &&
        err.status === 403 &&
        isStoredUserClient() &&
        isTenantOnlyUrl(req.url)
      ) {
        message.add({
          severity: 'warn',
          summary: translate.instant('common.error'),
          detail: translate.instant('auth.accessDenied'),
        });
        router.navigate(['/home']);
      }

      return throwError(() => err);
    }),
  );
};
